import React, { useState } from 'react';

interface TrustEntry {
  model: string;
  role: string;
  runs: number;
  successRate: number;
  avgCost: number;
  grade: 'A' | 'B' | 'C' | 'D' | 'F';
}

interface A2ACounterparty {
  id: string;
  endpoint: string;
  skill: string;
  lastSeen: number;
  trustScore: number;
  verified: boolean;
}

interface ComplianceControl {
  framework: string;
  control: string;
  status: 'pass' | 'warn' | 'fail' | 'na';
}

const TRUST_DATA: TrustEntry[] = [
  { model: 'claude-opus-4-7', role: 'builder', runs: 142, successRate: 0.94, avgCost: 0.42, grade: 'A' },
  { model: 'claude-opus-4-7', role: 'critic', runs: 98, successRate: 0.97, avgCost: 0.18, grade: 'A' },
  { model: 'claude-sonnet-4-6', role: 'builder', runs: 67, successRate: 0.88, avgCost: 0.12, grade: 'B' },
  { model: 'claude-sonnet-4-6', role: 'tester', runs: 54, successRate: 0.91, avgCost: 0.07, grade: 'A' },
  { model: 'claude-haiku-4-5', role: 'planner', runs: 211, successRate: 0.82, avgCost: 0.01, grade: 'B' },
  { model: 'ollama/llama3', role: 'builder', runs: 23, successRate: 0.61, avgCost: 0.00, grade: 'C' },
];

const A2A_DATA: A2ACounterparty[] = [
  { id: 'agent_9f2a', endpoint: 'http://localhost:5210', skill: 'recipe/ship', lastSeen: Date.now() - 3000, trustScore: 0.98, verified: true },
  { id: 'agent_4b7d', endpoint: 'https://vibe.example.com/a2a', skill: 'recipe/audit', lastSeen: Date.now() - 180000, trustScore: 0.87, verified: true },
  { id: 'agent_2c5e', endpoint: 'https://partner.dev/agent', skill: 'swe-bench/run', lastSeen: Date.now() - 7200000, trustScore: 0.54, verified: false },
];

const COMPLIANCE_DATA: ComplianceControl[] = [
  { framework: 'EU AI Act', control: 'Risk classification', status: 'pass' },
  { framework: 'EU AI Act', control: 'Human oversight (Art. 14)', status: 'pass' },
  { framework: 'EU AI Act', control: 'Logging & audit trail', status: 'pass' },
  { framework: 'EU AI Act', control: 'Transparency disclosure', status: 'warn' },
  { framework: 'NIST AI RMF', control: 'GOVERN — risk policy', status: 'pass' },
  { framework: 'NIST AI RMF', control: 'MAP — context classification', status: 'pass' },
  { framework: 'NIST AI RMF', control: 'MEASURE — drift detection', status: 'warn' },
  { framework: 'Colorado AI', control: 'Consequential decision log', status: 'pass' },
];

function gradeColor(g: TrustEntry['grade']): string {
  if (g === 'A') return 'var(--mint)';
  if (g === 'B') return 'var(--cyan)';
  if (g === 'C') return 'var(--gold)';
  return 'var(--coral)';
}

function statusBadge(s: ComplianceControl['status']) {
  const map: Record<ComplianceControl['status'], { label: string; color: string }> = {
    pass: { label: '✓ pass', color: 'var(--mint)' },
    warn: { label: '⚠ warn', color: 'var(--gold)' },
    fail: { label: '✗ fail', color: 'var(--coral)' },
    na: { label: '— n/a', color: 'var(--soft)' },
  };
  return map[s];
}

function fmtAgo(ts: number): string {
  const secs = (Date.now() - ts) / 1000;
  if (secs < 90) return `${Math.round(secs)}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

interface TrustViewProps {
  mode: 'beginner' | 'pro';
}

export function TrustView({ mode }: TrustViewProps) {
  const [budgetCap] = useState(10.0);
  const [budgetUsed] = useState(3.47);
  const budgetPct = (budgetUsed / budgetCap) * 100;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)',
        gridTemplateRows: 'auto auto',
        gap: 18,
        alignItems: 'start',
      }}
    >
      <section className="panel" style={{ padding: 22 }}>
        <div className="section-heading">
          <span className="section-kicker">Trust scores</span>
          <strong>Per-model / per-role letter grade</strong>
        </div>
        <div style={{ display: 'grid', gap: 10 }}>
          {TRUST_DATA.map((t) => (
            <div
              key={`${t.model}-${t.role}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '12px 14px',
                borderRadius: 12,
                border: '1px solid var(--line)',
                background: 'rgba(255,255,255,0.02)',
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: 'rgba(255,255,255,0.04)',
                  border: `2px solid ${gradeColor(t.grade)}`,
                  display: 'grid',
                  placeItems: 'center',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 700,
                  fontSize: '1rem',
                  color: gradeColor(t.grade),
                  flexShrink: 0,
                }}
              >
                {t.grade}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.82rem', color: 'var(--text)', marginBottom: 3 }}>
                  <span>{t.model.split('/').pop()}</span>
                  <span style={{ color: 'var(--muted)', marginLeft: 8 }}>({t.role})</span>
                </div>
                <div style={{ display: 'flex', gap: 14, fontSize: '0.76rem', color: 'var(--soft)' }}>
                  <span>{t.runs} runs</span>
                  <span>{(t.successRate * 100).toFixed(0)}% success</span>
                  <span>~${t.avgCost.toFixed(2)}/run</span>
                </div>
              </div>
              <div
                style={{
                  width: 50,
                  height: 5,
                  borderRadius: 3,
                  background: 'rgba(255,255,255,0.06)',
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <div
                  style={{
                    width: `${t.successRate * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    background: gradeColor(t.grade),
                    opacity: 0.8,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
        {mode === 'pro' && (
          <button type="button" className="ghost-button" style={{ marginTop: 14, width: '100%', fontSize: '0.8rem' }}>
            Export trust report
          </button>
        )}
      </section>

      <div style={{ display: 'grid', gap: 18 }}>
        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Budget</span>
            <strong>Spend cap &amp; status</strong>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 12,
            }}
          >
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.8rem', color: 'var(--cyan)' }}>
              ${budgetUsed.toFixed(2)}
            </span>
            <span style={{ color: 'var(--muted)', fontSize: '0.9rem' }}>of ${budgetCap.toFixed(2)} cap</span>
          </div>
          <div
            style={{
              height: 10,
              borderRadius: 5,
              background: 'rgba(255,255,255,0.06)',
              overflow: 'hidden',
              marginBottom: 12,
            }}
          >
            <div
              style={{
                width: `${budgetPct}%`,
                height: '100%',
                borderRadius: 5,
                background:
                  budgetPct > 80
                    ? 'linear-gradient(90deg, var(--coral), var(--gold))'
                    : 'linear-gradient(90deg, var(--cyan), var(--mint))',
              }}
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', color: 'var(--muted)' }}>
            <span>{budgetPct.toFixed(0)}% used this period</span>
            <span className={budgetPct > 80 ? 'health-hot' : 'health-steady'} style={{ fontFamily: 'var(--font-mono)' }}>
              {budgetPct > 80 ? 'near cap' : 'healthy'}
            </span>
          </div>
          {mode === 'pro' && (
            <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                Set hard cap
              </button>
              <button type="button" className="ghost-button" style={{ flex: 1, fontSize: '0.8rem' }}>
                CI gate
              </button>
            </div>
          )}
        </section>

        <section className="panel" style={{ padding: 22 }}>
          <div className="section-heading">
            <span className="section-kicker">Agent-to-Agent</span>
            <strong>A2A connections</strong>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {A2A_DATA.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 4,
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.76rem',
                      color: 'var(--text)',
                    }}
                  >
                    {c.id}
                  </span>
                  <span
                    style={{
                      fontSize: '0.72rem',
                      color: c.verified ? 'var(--mint)' : 'var(--gold)',
                      fontFamily: 'var(--font-mono)',
                    }}
                  >
                    {c.verified ? '✓ verified' : '⚠ unverified'}
                  </span>
                </div>
                <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginBottom: 4 }}>
                  {c.skill}
                  <span
                    style={{
                      marginLeft: 10,
                      color: 'var(--soft)',
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.72rem',
                    }}
                  >
                    trust {(c.trustScore * 100).toFixed(0)}%
                  </span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--soft)' }}>
                  {c.endpoint} · {fmtAgo(c.lastSeen)}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      <section className="panel" style={{ padding: 22, gridColumn: '1 / -1' }}>
        <div className="section-heading">
          <span className="section-kicker">Compliance</span>
          <strong>EU AI Act · NIST AI RMF · Colorado AI Act</strong>
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 10,
          }}
        >
          {COMPLIANCE_DATA.map((c) => {
            const badge = statusBadge(c.status);
            return (
              <div
                key={`${c.framework}-${c.control}`}
                style={{
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: '1px solid var(--line)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  style={{
                    fontSize: '0.7rem',
                    color: 'var(--soft)',
                    fontFamily: 'var(--font-mono)',
                    marginBottom: 5,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                  }}
                >
                  {c.framework}
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ fontSize: '0.82rem', color: 'var(--text)' }}>{c.control}</span>
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: '0.76rem',
                      color: badge.color,
                    }}
                  >
                    {badge.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        {mode === 'pro' && (
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button type="button" className="ghost-button" style={{ fontSize: '0.8rem' }}>
              Export compliance zip
            </button>
            <button type="button" className="ghost-button" style={{ fontSize: '0.8rem' }}>
              Verify attestation
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
